import React, { useEffect } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { Navigate, useLocation } from 'react-router-dom';

import { useConnectionStatus } from '@/api/auth';
import { useUserAccount } from '@/api/userAccount';
import { MainContentLoader } from '@/components/loaders/MainContentLoader';
import { useLoginRedirectUrl } from '@/hooks/useLoginRedirectUrl';
import { useGlobalStore } from '@/store';

const publicRoutes = ['/login', '/signup'];

export type AuthProviderProps = {
    /**
     * With optional=true, this will still maintain the GlobalStore isLoggedIn, but
     * doesn't redirect on not logged in. Children are rendered once isLoggedIn
     * is resolved, either way.
     */
    optional?: boolean;
    /**
     * Default loadingChild is a full screen splash
     */
    loadingChild?: React.ReactNode;
    children: React.ReactNode;
};

export const AuthProvider = ({ optional, loadingChild, children }: AuthProviderProps) => {
    const loginUrl = useLoginRedirectUrl();
    const location = useLocation();
    const isLoggedIn = useGlobalStore((state) => state.isLoggedIn);
    const setIsLoggedIn = useGlobalStore((state) => state.setIsLoggedIn);
    const { isPending, data, error, refetch, isStale } = useConnectionStatus({
        config: {
            staleTime: 1000 * 60 * 15, // 15 minutes
            refetchOnWindowFocus: 'always',
        },
    });
    const mixpanel = useMixpanel();
    const { data: accountData } = useUserAccount();

    useEffect(() => {
        if (isPending) {
            return;
        }
        if (error) {
            setIsLoggedIn(false);
            return;
        }
        if (data?.status === 'success') {
            setIsLoggedIn(true);
            if (accountData?.data?.user?.username) {
                mixpanel?.identify(accountData.data.user.username);
            }
            return;
        }

        // Should never come to this point
        console.warn('AuthProvider: Cannot verify user authentication status');
    }, [isPending, data, error, setIsLoggedIn, accountData?.data?.user?.username, mixpanel]);

    useEffect(() => {
        // check on every route change but refetch only once every 15 minutes
        if (isStale) {
            refetch();
        }
    }, [location.pathname, isStale, refetch]);

    if (isPending) {
        return loadingChild ?? <MainContentLoader message="Confirming user authentication status..." />;
    }

    if (
        !optional &&
        !isLoggedIn &&
        !publicRoutes.includes(location.pathname) &&
        data?.status !== 'success' &&
        process.env.REACT_APP_DISABLE_LOGGED_IN_CHECK !== 'true'
    ) {
        return <Navigate to={loginUrl} replace />;
    }

    return children;
};
